import clsx from "clsx"
import { RefObject, createContext, useEffect, useRef, useState } from "react"
import DraggableBottomSheet from "react-draggable-bottom-sheet"
import { useHotkeys } from "react-hotkeys-hook"

import useLayerStack from "@hooks/useLayerStack"

import styles from "./BottomSheet.module.scss"
import { BottomSheetContextValues, BottomSheetProps } from "./BottomSheet.types"
import BottomSheetContent from "./BottomSheetContent/BottomSheetContent"
import BottomSheetDivider from "./BottomSheetDivider/BottomSheetDivider"
import BottomSheetDragIndicator from "./BottomSheetDragIndicator/BottomSheetDragIndicator"
import BottomSheetFooter from "./BottomSheetFooter/BottomSheetFooter"
import BottomSheetItem from "./BottomSheetItem/BottomSheetItem"

export const BottomSheetContext = createContext<BottomSheetContextValues | undefined>(undefined)
BottomSheetContext.displayName = "BottomSheet"

interface BottomSheetSurfaceProps extends BottomSheetProps {
    contentHeight: number | undefined
    handleClose: () => void
    innerRef: RefObject<HTMLDivElement>
    overlayZIndex: number
    surfaceZIndex: number
}

function BottomSheetSurface(props: BottomSheetSurfaceProps) {
    const {
        children,
        className,
        contentHeight,
        handleClose,
        innerRef,
        isFullHeight = false,
        isOpen,
        overlayZIndex,
        surfaceZIndex,
    } = props

    return (
        <DraggableBottomSheet
            isOpen={isOpen}
            close={handleClose}
            classNames={{
                backdrop: styles.backdrop,
                dragIndicator: {
                    wrap: styles.dragIndicatorWrap,
                },
                window: {
                    wrap: styles.window,
                    content: styles.content,
                },
            }}
            styles={{
                bottomSheet: { zIndex: overlayZIndex },
                backdrop: { zIndex: overlayZIndex },
                window: {
                    wrap: { zIndex: surfaceZIndex },
                    content: {
                        height: isFullHeight ? window.innerHeight : contentHeight,
                    },
                },
            }}
        >
            <div
                ref={innerRef}
                className={clsx(className, {
                    [styles.fullHeight]: isFullHeight,
                })}
            >
                {children}
            </div>
        </DraggableBottomSheet>
    )
}

function BottomSheet(props: BottomSheetProps) {
    const { children, isOpen, onClose, isFullHeight = false, className } = props

    const [contentHeight, setContentHeight] = useState<number>()
    const [contentActive, setContentActive] = useState<string>()
    const [isContentOverflowing, setIsContentOverflowing] = useState<boolean>(false)

    const handleClose = () => {
        setContentActive(undefined)
        onClose()
    }

    useHotkeys("esc", handleClose, { enabled: isOpen })

    const ref = useRef<HTMLDivElement>(null)

    const { overlayZIndex, surfaceZIndex } = useLayerStack(isOpen)

    useEffect(() => {
        const allFocusableElements = Array.from(ref.current?.querySelectorAll('[tabindex="0"]') ?? [])
        const firstFocusableElement = allFocusableElements[0] as HTMLElement
        const lastFocusableElement = allFocusableElements[allFocusableElements.length - 1] as HTMLElement

        const onKeyDown = (event: KeyboardEvent) => {
            const isTabPressed = event.key === "Tab" || event.keyCode === 9
            const isTabAndShiftPressed = isTabPressed && event.shiftKey
            const isOnlyTabPressed = isTabPressed && !event.shiftKey
            const isOnTheFirstElement = document.activeElement === firstFocusableElement
            const isOnTheLastElement = document.activeElement === lastFocusableElement
            const isOutsideFocusableElements =
                !document.activeElement || !allFocusableElements.includes(document.activeElement)

            if (!isTabPressed) {
                return
            }

            if (isOutsideFocusableElements) {
                firstFocusableElement?.focus()
                event.preventDefault()
            }

            if (isTabAndShiftPressed && isOnTheFirstElement) {
                lastFocusableElement?.focus()
                event.preventDefault()
            }

            if (isOnlyTabPressed && isOnTheLastElement) {
                firstFocusableElement?.focus()
                event.preventDefault()
            }
        }

        if (isOpen) {
            document.addEventListener("keydown", onKeyDown)
        } else {
            document.removeEventListener("keydown", onKeyDown)
        }

        return () => {
            document.removeEventListener("keydown", onKeyDown)
        }
    }, [isOpen, contentActive])

    return (
        <BottomSheetContext.Provider
            value={{
                contentHeight,
                setContentHeight,
                isOpen,
                contentActive,
                setContentActive,
                isContentOverflowing,
                setIsContentOverflowing,
            }}
        >
            <BottomSheetSurface
                isOpen={isOpen}
                onClose={onClose}
                isFullHeight={isFullHeight}
                className={className}
                contentHeight={contentHeight}
                handleClose={handleClose}
                innerRef={ref}
                overlayZIndex={overlayZIndex}
                surfaceZIndex={surfaceZIndex}
            >
                {children}
            </BottomSheetSurface>
        </BottomSheetContext.Provider>
    )
}

const Root = BottomSheet
const Item = BottomSheetItem
const Divider = BottomSheetDivider
const Content = BottomSheetContent
const DragIndicator = BottomSheetDragIndicator
const Footer = BottomSheetFooter

export default { Root, Item, Divider, Content, DragIndicator, Footer }
